import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Users, Eye, Loader2, Phone, Check, X, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TIER_BADGE_STYLE = {
  Silver:   { background: "linear-gradient(145deg, #F4F7FA 0%, #B6C2D0 55%, #6F7A8A 100%)", color: "#1A2530", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 2px rgba(0,0,0,0.15)" },
  Gold:     { background: "linear-gradient(145deg, #FBE38B 0%, #D4A93A 55%, #8C6618 100%)", color: "#FFFFFF", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 1px 2px rgba(0,0,0,0.15)" },
  Platinum: { background: "linear-gradient(145deg, #2B50B0 0%, #173075 55%, #0A1736 100%)", color: "#FFFFFF", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 1px 2px rgba(0,0,0,0.2)" },
};
const tierBadgeStyle = (t) => TIER_BADGE_STYLE[t] || TIER_BADGE_STYLE.Silver;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [mobileEditing, setMobileEditing] = useState(null); // user obj being edited
  const [newMobile, setNewMobile] = useState("");
  const [mobileSaving, setMobileSaving] = useState(false);
  const [mobileMsg, setMobileMsg] = useState(null);

  const openMobileEdit = (u) => { setMobileEditing(u); setNewMobile(u.mobile || ""); setMobileMsg(null); };
  const saveMobile = async () => {
    const trimmed = (newMobile || "").trim();
    if (trimmed.length < 10) { setMobileMsg({ type: "error", text: "Mobile must be at least 10 digits" }); return; }
    if (trimmed === mobileEditing?.mobile) { setMobileMsg({ type: "error", text: "This is already the current mobile number" }); return; }
    setMobileSaving(true); setMobileMsg(null);
    try {
      await API.put(`/admin/users/${mobileEditing.id}/mobile`, { mobile: trimmed });
      setMobileMsg({ type: "success", text: "Mobile updated successfully" });
      setTimeout(() => { setMobileEditing(null); load(); }, 700);
    } catch (e) {
      setMobileMsg({ type: "error", text: e.response?.data?.detail || "Failed to update mobile" });
    } finally { setMobileSaving(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set("search", search);
      if (tier) params.set("tier", tier);
      const { data } = await API.get(`/admin/users?${params}`);
      setUsers(data.users || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  }, [search, tier, page]);

  useEffect(() => { load(); }, [load]);

  const viewUser = async (userId) => {
    try {
      const { data } = await API.get(`/admin/users/${userId}`);
      setSelected(data);
    } catch {}
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Users ({total})</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage registered customers</p>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="user-search" placeholder="Search by name, mobile, email..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-10 h-10 rounded-xl border-gray-200 focus:border-[#008A99] focus:ring-[#008A99]" />
        </div>
        <select data-testid="tier-filter" value={tier} onChange={(e) => { setTier(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-gray-200 px-3 text-sm bg-white focus:border-[#008A99]">
          <option value="">All Tiers</option>
          <option value="Silver">Silver</option>
          <option value="Gold">Gold</option>
          <option value="Platinum">Platinum</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Mobile</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Tier</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Points</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Scans</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">KYC</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium text-[#0F172A]">{u.full_name || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{u.mobile}</td>
                    <td className="px-4 py-3"><span className="text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wider inline-block" style={tierBadgeStyle(u.tier || "Silver")} data-testid={`tier-badge-${u.id}`}>{u.tier || "Silver"}</span></td>
                    <td className="px-4 py-3 text-right font-semibold">{(u.balance_points || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">{u.total_scans || 0}</td>
                    <td className="px-4 py-3">{u.kyc_completed ? <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Done</span> : <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Pending</span>}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => viewUser(u.id)} className="text-[#008A99] hover:bg-[#E0F4F5] p-1.5 rounded-lg transition-colors" data-testid={`view-user-${u.id}`} title="View details">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => openMobileEdit(u)} className="text-amber-600 hover:bg-amber-50 p-1.5 rounded-lg transition-colors" data-testid={`edit-mobile-${u.id}`} title="Change mobile number">
                          <Phone className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No users found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 text-sm rounded-xl border disabled:opacity-40">Prev</button>
          <span className="px-4 py-2 text-sm">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 text-sm rounded-xl border disabled:opacity-40">Next</button>
        </div>
      )}

      {/* User Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>User Details</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500">Name:</span><p className="font-medium">{selected.full_name || "-"}</p></div>
                <div><span className="text-slate-500">Mobile:</span><p className="font-medium">{selected.mobile}</p></div>
                <div><span className="text-slate-500">Email:</span><p className="font-medium">{selected.email || "-"}</p></div>
                <div><span className="text-slate-500">Tier:</span><p className="font-medium">{selected.tier}</p></div>
                <div><span className="text-slate-500">Balance:</span><p className="font-bold text-[#008A99]">{(selected.balance_points || 0).toLocaleString()} pts</p></div>
                <div><span className="text-slate-500">Total Scans:</span><p className="font-medium">{selected.total_scans || 0}</p></div>
                <div><span className="text-slate-500">Lab:</span><p className="font-medium">{selected.lab_name || "-"}</p></div>
                <div><span className="text-slate-500">City:</span><p className="font-medium">{selected.city || "-"}, {selected.state || "-"}</p></div>
                <div className="col-span-2"><span className="text-slate-500">Registry ID:</span><p className="font-bold text-[#008A99]">{selected.registry_id || "—"}</p></div>
              </div>
              {((selected.instruments && selected.instruments.length > 0) || (selected.instrument_data && Object.keys(selected.instrument_data || {}).length > 0)) && (
                <div data-testid="admin-user-instruments">
                  <h4 className="font-semibold mb-2 flex items-center justify-between">
                    <span>Mapped Instruments (from ERP)</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#008A99] text-white">
                      {(selected.instruments?.length) || (selected.instrument_data ? 1 : 0)} ITEM(S)
                    </span>
                  </h4>
                  <div className="max-h-72 overflow-y-auto space-y-2">
                    {((selected.instruments?.length > 0) ? selected.instruments : [selected.instrument_data]).map((inst, idx) => (
                      <div key={idx} className="text-xs bg-slate-50 rounded-lg p-3 border-l-2 border-[#008A99]/40">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-bold text-[#008A99]">{inst.PRODUCT_SHORT_NAME || inst.PRODUCT_SUB_CATEGORY || "—"}</p>
                          {inst.CONTRACT_STATUS && (
                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${
                              String(inst.CONTRACT_STATUS).toUpperCase() === "ACTIVE" ? "bg-green-100 text-green-700" :
                              String(inst.CONTRACT_STATUS).toUpperCase() === "EXPIRED" ? "bg-red-100 text-red-700" :
                              "bg-amber-100 text-amber-700"
                            }`}>{inst.CONTRACT_STATUS}</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                          {inst.PRODUCT_CATEGORY && <div><span className="text-slate-400">Category:</span> {inst.PRODUCT_CATEGORY}</div>}
                          {inst.SERIAL_NUMBER && <div><span className="text-slate-400">Serial:</span> {inst.SERIAL_NUMBER}</div>}
                          {inst.ITEM_CODE && <div><span className="text-slate-400">Item:</span> {inst.ITEM_CODE}</div>}
                          {inst.PARTY_NUMBER && <div><span className="text-slate-400">Party:</span> {inst.PARTY_NUMBER}</div>}
                          {inst.CONTRACT_NUMBER && <div className="col-span-2"><span className="text-slate-400">Contract:</span> {inst.CONTRACT_NUMBER} ({inst.CONTRACT_TYPE || "—"})</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selected.scans?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Recent Scans</h4>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {selected.scans.map((s, i) => (
                      <div key={i} className="flex justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                        <span>{s.product_name || s.product_code}</span>
                        <span className="text-emerald-600 font-semibold">+{s.points_earned}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Mobile Change Dialog */}
      <Dialog open={!!mobileEditing} onOpenChange={(o) => !o && setMobileEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Change Mobile Number</DialogTitle></DialogHeader>
          {mobileEditing && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>This bypasses OTP verification. Use only when the customer cannot access their phone.</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500 text-xs">User:</span><p className="font-medium">{mobileEditing.full_name || "—"}</p></div>
                <div><span className="text-slate-500 text-xs">Current mobile:</span><p className="font-medium">{mobileEditing.mobile || "—"}</p></div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">New Mobile</label>
                <input
                  type="tel" inputMode="numeric" maxLength={10}
                  value={newMobile} onChange={e => setNewMobile(e.target.value.replace(/\D/g, ""))}
                  className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99]"
                  data-testid="admin-new-mobile-input"
                  placeholder="10-digit number"
                />
              </div>
              {mobileMsg && (
                <div className={`text-xs px-3 py-2 rounded-lg flex items-center gap-1.5 ${mobileMsg.type === "error" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="admin-mobile-msg">
                  {mobileMsg.type === "error" ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                  {mobileMsg.text}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setMobileEditing(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-lg font-semibold text-slate-700">Cancel</button>
                <button onClick={saveMobile} disabled={mobileSaving} className="px-4 py-2 text-sm bg-[#008A99] text-white rounded-lg font-semibold disabled:opacity-50 flex items-center gap-1" data-testid="admin-save-mobile-btn">
                  {mobileSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
